<template>
  <div>
    <bg-loader :dark="true" v-if="isLoading"></bg-loader>
    <div v-if="!isLoading">
      <router-link
        class="link-back"
        :to="`/test/${$route.params.id}/suggestions`"
      >
        <div>&larr; Back to suggestions</div>
      </router-link>
      <div
        style="display: flex; justify-content: space-between; align-items: center;"
      >
        <h1
          v-bg-typography="'h1'"
          style="margin-top: 10px; margin-bottom: 10px;"
          v-if="suggestion && !newSuggestion"
        >
          {{ suggestion.rank }}. {{ suggestion.title }}
        </h1>
        <h1
          v-bg-typography="'h1'"
          style="margin-top: 10px; margin-bottom: 10px;"
          v-else
        >
          New suggestion
        </h1>
        <div class="arrows">
          <div class="actions-group" v-if="editMode">
            <BgButton id="save" text="Save" @click="saveSuggestion" />
          </div>
          <div
            class="donebutton"
            v-bind:class="{ done: suggestion.done }"
            @click="markAsDone"
            v-if="!editMode"
          >
            <bg-icon name="check"></bg-icon>
          </div>
          <div class="edit-button" @click="editMode = true" v-if="!editMode">
            <bg-icon name="fedit"></bg-icon>
          </div>
          <div class="delete-button" @click="deleteSuggestion" v-if="!editMode">
            <bg-icon name="fdelete"></bg-icon>
          </div>
          <div v-if="!editMode" class="arrow" @click="previous()">
            <bg-icon name="chevronLeft"></bg-icon>
          </div>
          <div v-if="!editMode" class="arrow" @click="next()">
            <bg-icon name="chevronRight"></bg-icon>
          </div>
        </div>
      </div>
      <p v-if="suggestion.insight_id">
        Based on Insight:
        <u
          ><span
            style="cursor: pointer;"
            @click="
              $router.push(
                `/test/${suggestion.test_id}/insights/${suggestion.insight_id}`
              )
            "
            >{{
              insights.find((el) => el.id === suggestion.insight_id).title
            }}</span
          ></u
        >
      </p>

      <div class="read" v-if="!editMode">
        <h3 v-bg-typography="'h3'" style="margin-bottom: 5px;">
          Prioritization
        </h3>
        <div class="prio">
          <div class="bluecard">
            <p style="font-size: 10pt;">Effort</p>
            <p
              v-if="suggestion.effort === 1"
              style="font-size: 16pt; font-weight: 600; color: #00DEAB;"
            >
              Low
            </p>
            <p
              v-else-if="suggestion.effort === 2"
              style="font-size: 16pt; font-weight: 600; color: #F1C40F;"
            >
              Medium
            </p>
            <p
              v-else-if="suggestion.effort === 3"
              style="font-size: 16pt; font-weight: 600; color: #FF14A9;"
            >
              High
            </p>
            <p v-else style="font-size: 12pt; font-weight: 600">-</p>
          </div>
          <div class="bluecard">
            <p style="font-size: 10pt;">Impact</p>
            <p
              v-if="suggestion.impact === 1"
              style="font-size: 16pt; font-weight: 600; color: #00DEAB;"
            >
              Low
            </p>
            <p
              v-else-if="suggestion.impact === 2"
              style="font-size: 16pt; font-weight: 600; color: #F1C40F;"
            >
              Medium
            </p>
            <p
              v-else-if="suggestion.impact === 3"
              style="font-size: 16pt; font-weight: 600; color: #FF14A9;"
            >
              High
            </p>
            <p v-else style="font-size: 12pt; font-weight: 600">-</p>
          </div>
          <div class="bluecard">
            <p style="font-size: 10pt;">Next step</p>
            <p style="font-size: 16pt; font-weight: 600">
              {{ this.optionsSteps[suggestion.next_step - 1] }}
            </p>
          </div>
          <div class="bluecard">
            <p style="font-size: 10pt;">Owner</p>
            <p
              style="font-size: 16pt; font-weight: 600"
              v-if="suggestion.owner_id && !isLoadingUsers"
            >
              {{
                optionOwners.find((el) => el.id === suggestion.owner_id).profile
                  .first_name +
                  " " +
                  optionOwners.find((el) => el.id === suggestion.owner_id)
                    .profile.last_name
              }}
            </p>
            <div v-if="isLoadingUsers">
              <div class="lds-ellipsis">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
            <p v-if="!isLoadingUsers && !suggestion.owner_id">-</p>
          </div>
        </div>

        <h3
          v-bg-typography="'h3'"
          style="margin-bottom: 5px; margin-top: 25px;"
        >
          Suggestion
          <span
            style="font-weight: 500"
            v-if="
              suggestion.author_id &&
                optionOwners.find((el) => el.id === suggestion.author_id)
            "
            >by
            {{
              optionOwners.find((el) => el.id === suggestion.author_id).profile
                .first_name +
                " " +
                optionOwners.find((el) => el.id === suggestion.author_id)
                  .profile.last_name
            }}</span
          >
        </h3>
        <div class="sug-text">
          <p
            v-if="suggestion.suggestion"
            v-html="suggestion.suggestion"
            class="textbox"
          ></p>
          <p v-else class="textbox">-</p>
        </div>

        <h3
          v-bg-typography="'h3'"
          style="margin-bottom: 5px; margin-top: 25px;"
        >
          Additional info
        </h3>
        <div class="sug-text">
          <p
            v-if="suggestion.additional_info"
            v-html="suggestion.additional_info"
            class="textbox"
          ></p>
          <p v-else class="textbox">-</p>
        </div>
      </div>

      <div class="edit" v-if="editMode">
        <bg-field
          label="Title of this suggestion"
          name="url"
          style="max-width: 350px; margin-bottom: 20px;"
        >
          <bg-input
            height="46px"
            name="url"
            placeholder="Short title"
            v-model="suggestion.title"
          />
        </bg-field>
        <Editor
          :whiteBackground="true"
          :loadContent="suggestion.suggestion"
          @update="updateSuggestion"
          title="Suggestion"
        ></Editor>
        <Editor
          :whiteBackground="true"
          :loadContent="suggestion.additional_info"
          @update="updateAdditional"
          title="Additional info"
        ></Editor>
        <bg-field
          style="margin-top: 20px;"
          info="This suggestion is based on data coming from which Insight?"
          label="Insight"
          name="insight"
        >
          <bg-select
            :options="insights"
            placeholder="Select an Insight"
            @change="updateInsight"
            label="title"
            :value="selectedInsight"
            :extraWide="true"
            :clearable="true"
          ></bg-select>
        </bg-field>
        <div class="bottom">
          <bg-field label="Effort">
            <bg-select
              name="effort"
              :options="lowHigh"
              label="text"
              placeholder="Low / Medium /High"
              @change="updateEffort"
              :value="lowHigh[suggestion.effort - 1]"
            ></bg-select>
          </bg-field>
          <bg-field label="Impact">
            <bg-select
              name="impact"
              :options="lowHigh"
              label="text"
              placeholder="Low / Medium / High"
              @change="updateImpact"
              :value="lowHigh[suggestion.impact - 1]"
            ></bg-select>
          </bg-field>
          <bg-field label="Next step" name="nextstep">
            <bg-select
              :options="optionsSteps"
              placeholder="Pick a next step"
              @change="updateStep"
              :value="optionsSteps[suggestion.next_step - 1]"
            ></bg-select>
          </bg-field>
          <bg-field label="Owner" name="owner">
            <bg-select
              v-if="!isLoadingUsers"
              :options="optionOwners"
              placeholder="Who will conquer this next step?"
              label="email"
              @change="updateOwner"
              :value="optionOwners.find((el) => el.id === suggestion.owner_id)"
            ></bg-select>
            <div v-else>
              <div class="lds-ellipsis">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
          </bg-field>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BgTypography from "@/directives/bg-typography";
import BgButton from "@/components/bg-button";
import BgIcon from "@/components/bg-icon";
import Editor from "@/components/editor";
import BgSelect from "@/components/bg-select";
import BgField from "@/components/bg-field";
import BgInput from "@/components/bg-input";
import API from "@/api";
import BgLoader from "@/components/bg-loader";
import { mapState } from "vuex";

export default {
  directives: {
    BgTypography,
  },
  components: {
    BgButton,
    BgIcon,
    Editor,
    BgSelect,
    BgField,
    BgInput,
    BgLoader,
  },
  data() {
    return {
      editMode: false,
      isLoading: true,
      isLoadingUsers: true,
      insights: [],
      optionOwners: ["Max van Kaathoven", "Joan Planas"],
      optionsSteps: ["Implement", "A/B test", "Research", "Nothing"],
      lowHigh: [
        {
          text: "Low",
          value: 1,
        },
        {
          text: "Medium",
          value: 2,
        },
        {
          text: "High",
          value: 3,
        },
      ],
      optionsMetrics: ["CTR", "Conversion"],
      optionsSignificance: [
        "Positive significant",
        "Negative significant",
        "Positive but not significant",
        "Negative but not significant",
      ],
      optionsSource: ["A/B test", "Research"],
      options: ["this is an insight"],
      selectedInsight: null,
      suggestion: {
        additional_info: "",
        rank: null,
        suggestion: "",
        impact: null,
        owner: null,
        next_step: null,
        effort: null,
        title: "",
        done: false,
      },
      suggestions: [],
    };
  },
  created() {
    this.fetchData();
  },
  watch: {
    '$route.params.suggestionId'() {
      this.isLoading = true;
      this.fetchData();
    },
    '$route.params.id'() {
      this.isLoading = true;
      this.fetchData();
    },
  },
  computed: {
    ...mapState({
      usersState: (state) => state.users,
      testState: (state) => state.test,
    }),
    newSuggestion() {
      return this.$route.params.suggestionId === "new";
    },
  },
  methods: {
    ensureTestLoaded: async function() {
      const testId = this.$route.params.id;
      if (!this.testState || (this.testState && String(this.testState.id) !== String(testId))) {
        try { await this.$store.dispatch("fetchTest", testId); } catch(e) {}
      }
    },
    normalizeSuggestion(input) {
      const suggestion = { ...input };
      if (!suggestion) return input;
      // Ensure non-null strings
      suggestion.additional_info = suggestion.additional_info || "";
      suggestion.suggestion = suggestion.suggestion || "";
      suggestion.title = suggestion.title || "";
      // Coerce effort/impact from strings to numeric scale
      const lowHighMap = { low: 1, medium: 2, high: 3 };
      if (typeof suggestion.effort === "string") {
        suggestion.effort = lowHighMap[suggestion.effort.toLowerCase()] || null;
      }
      if (typeof suggestion.impact === "string") {
        suggestion.impact = lowHighMap[suggestion.impact.toLowerCase()] || null;
      }
      // Coerce next_step from string to index
      const stepMap = { implement: 1, "a/b test": 2, research: 3, nothing: 4 };
      if (typeof suggestion.next_step === "string") {
        suggestion.next_step = stepMap[suggestion.next_step.toLowerCase()] || null;
      }
      return suggestion;
    },
    async deleteSuggestion() {
      let r = confirm("Are you sure?!");
      if (r == true) {
        const res = await API.test.deleteSuggestion(
          this.$route.params.id,
          this.$route.params.suggestionId
        );
        this.$router.push(`/test/${this.$route.params.id}/suggestions`);
      }
    },
    async fetchData() {
      await this.ensureTestLoaded();
      const testId = this.$route.params.id;
      const suggestionParamId = this.$route.params.suggestionId;
      const insights = await API.test.getInsights(testId);
      this.insights = insights.data.insights;

      if (!this.newSuggestion) {
        let loaded = null;
        try {
          const res = await API.test.getSuggestion(testId, suggestionParamId);
          loaded = res && res.suggestion ? res.suggestion : res;
        } catch (e) {
          loaded = null;
        }

        // Fallback to list if show endpoint is unavailable or wrapped unexpectedly
        if (!loaded || !loaded.id) {
          const listRes = await API.test.getSuggestions(testId);
          this.suggestions = listRes.suggestions;
          loaded = this.suggestions.find(
            (el) => String(el.id) === String(suggestionParamId)
          );
        } else {
          // Also keep suggestions list for next/previous navigation
          const listRes = await API.test.getSuggestions(testId);
          this.suggestions = listRes.suggestions;
        }

        if (loaded) {
          this.suggestion = this.normalizeSuggestion(loaded);
          if (this.suggestion.insight_id) {
            this.selectedInsight = this.insights.find(
              (el) => el.id === this.suggestion.insight_id
            );
          }
        }
        this.editMode = false;
      } else {
        this.editMode = true;
      }
      this.isLoading = false;

      this.isLoadingUsers = true;
      try {
        if (this.testState && this.testState.company) {
          const users = await API.user.getCompanyUsers(this.testState.company.id);
          this.optionOwners = users.users;
        } else {
          this.optionOwners = [];
        }
      } catch(e) {
        this.optionOwners = [];
      }
      this.isLoadingUsers = false;
    },
    async saveSuggestion() {
      this.isLoading = true;
      if (this.newSuggestion) {
        console.log("new");
        this.suggestion.test_id = this.$route.params.id;
        this.suggestion.author_id = this.$store.state.user.user.id;
        const suggestion = await API.test.createSuggestion(this.suggestion);
        console.log(suggestion);
        this.$router.push(`/test/${this.$route.params.id}/suggestions`);
      } else {
        const res = await API.test.updateSuggestion(this.suggestion);
      }

      this.editMode = false;
      this.isLoading = false;
    },
    markAsDone() {
      this.suggestion.done = !this.suggestion.done;
      this.saveSuggestion();
    },
    next() {
      let next = this.suggestions.find(
        (el) => el.rank === this.suggestion.rank + 1
      );

      if (next) {
        this.$router.push(
          `/test/${this.$route.params.id}/suggestions/${next.id}`
        );
      }
    },
    previous() {
      let previous = this.suggestions.find(
        (el) => el.rank === this.suggestion.rank - 1
      );

      if (previous) {
        this.$router.push(
          `/test/${this.$route.params.id}/suggestions/${previous.id}`
        );
      }
    },
    updateInsight(val) {
      this.selectedInsight = val;
      if (val != null) {
        this.suggestion.insight_id = val.id;
      } else {
        this.suggestion.insight_id = val;
      }
    },
    updateEffort(val) {
      console.log(1);
      this.suggestion.effort = val.value;
    },
    updateImpact(val) {
      console.log(2);
      this.suggestion.impact = val.value;
    },
    updateAdditional(val) {
      this.suggestion.additional_info = val;
    },
    updateSuggestion(val) {
      this.suggestion.suggestion = val;
    },
    updateStep(val) {
      this.suggestion.next_step =
        this.optionsSteps.findIndex((el) => el === val) + 1;
    },
    updateOwner(val) {
      this.suggestion.owner_id = val.id;
    },
  },
};
</script>

<style scoped lang="scss">
.buttons {
  display: flex;
  gap: 10px;
  margin-top: 25px;
  margin-bottom: 25px;
}

#add {
  background-color: #0b0b3d;
  border: none;
}
#add:hover {
  border: none;
  &:hover {
    box-shadow: 0px 0px 0px 3px rgba(209, 219, 229, 1);
  }
}

#check {
  background-color: #00deab;
  border: none;
  img,
  svg {
    color: #0b0b3d;
  }
}

.read {
  margin-top: 25px;
}
.edit {
  margin-top: 25px;
}
.prio {
  display: flex;
  gap: 15px;
  max-width: 100%;
}
.bluecard {
  p {
    color: white;
  }

  width: 100%;
  background-color: #636383;
  padding: 25px;
  border-radius: 6px;
  border: 2px solid #b1b1c1;

  &:last-child {
    width: 200%;
  }
}

.textbox {
  padding: 20px;
  background-color: white;
  border-radius: 6px;
}

.bottom {
  display: flex;
  gap: 15px;
}

.resultcard {
  padding: 20px;
  background: rgba(0, 222, 171, 0.25);
  border: 2px solid #00deab;
  border-radius: 6px;
  margin-top: 10px;
}

.addresult {
  p {
    padding: 13px;
    background-color: #0b0b3d;
    color: white;
    font-weight: 600;
    width: 130px;
    text-align: center;
    margin-top: 30px;
    border-radius: 6px;

    &:hover {
      box-shadow: 0px 0px 0px 3px rgba(209, 219, 229, 1);
    }
  }
}
.arrows {
  display: flex;
  gap: 10px;
  align-items: center;
}
.arrows .actions-group {
  display: flex;
  align-items: center;
  gap: 10px;
}
.arrows .arrow {
  cursor: pointer;
  width: 45px;
  height: 45px;
  background-color: white;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.arrow:hover {
  box-shadow: 0px 0px 0px 3px rgba(209, 219, 229, 1);
}

.edit-button {
  border: 1px solid #0b0b3d;
  cursor: pointer;
  width: 45px;
  height: 45px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    box-shadow: 0px 0px 0px 3px rgba(209, 219, 229, 1);
    background-color: #0b0b3d;

    svg {
      color: white;
    }
  }
}

.donebutton {
  border: 1px solid #0b0b3d;
  cursor: pointer;
  width: 45px;
  height: 45px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    box-shadow: 0px 0px 0px 3px rgba(209, 219, 229, 1);
    background-color: #0b0b3d;

    svg {
      color: white;
    }
  }
}

.donebutton.done {
  background-color: #00deab;
  &:hover {
    box-shadow: 0px 0px 0px 3px rgba(209, 219, 229, 1);
    background-color: #00deab;

    svg {
      color: #0b0b3d;
    }
  }
}
.delete-button {
  border: 1px solid #0b0b3d;
  cursor: pointer;
  width: 45px;
  height: 45px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 15px;

  &:hover {
    box-shadow: 0px 0px 0px 3px rgba(209, 219, 229, 1);
    background-color: #ff14a9;
    border: 1px solid #ff14a9;
  }
}

.lds-ellipsis {
  display: inline-block;
  position: relative;
  width: 20px;
  height: 20px;
}
.lds-ellipsis div {
  position: absolute;
  top: 12px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #fff;
  animation-timing-function: cubic-bezier(0, 1, 1, 0);
}
.lds-ellipsis div:nth-child(1) {
  left: 8px;
  animation: lds-ellipsis1 0.6s infinite;
}
.lds-ellipsis div:nth-child(2) {
  left: 8px;
  animation: lds-ellipsis2 0.6s infinite;
}
.lds-ellipsis div:nth-child(3) {
  left: 32px;
  animation: lds-ellipsis2 0.6s infinite;
}
.lds-ellipsis div:nth-child(4) {
  left: 56px;
  animation: lds-ellipsis3 0.6s infinite;
}
@keyframes lds-ellipsis1 {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}
@keyframes lds-ellipsis3 {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
  }
}
@keyframes lds-ellipsis2 {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(24px, 0);
  }
}
</style>
